import React, {useCallback, useMemo, useRef, useState} from 'react';
import './styles.scss';
import moment from 'moment';
import strings from '../../../utils/strings';
import {updateObject, updateItemInArray} from '../../../utils/reducerUtils';

const genders = [
	{label: 'Mädchen', value: 'f'},
	{label: 'Junge', value: 'm'},
];

const yesAndNo = [
	{label: 'Nein', value: 'no'},
	{label: 'Ja', value: 'yes'},
];

const HOME_LANGUAGE = 'homeLanguage';
const GREW_UP_IN_BILINGUAL_HOME = 'grewUpInBilingualHome';

const initialTestUserInfo = {
	firstName: '',
	lastName: '',
	gender: '',
	email: '',
	testDevice: '',
	birthDate: '',
	age: {years: 0, months: 0},
	education: {youthGrade: ''}, // keep as string for <select>, cast when needed
	homeLanguage: {grewUpInBilingualHome: false},
	isForeign: '', // keep alongside existing use
};

function CandidateInfo(props) {
	const [isFormValid, setIsFormValid] = useState(false);
	const [isNorming, setIsNorming] = useState(0);
	const [excludeReading, setExcludeReading] = useState('no'); // align with yes/no radio
	const [dateIsWrong, setDateIsWrong] = useState(false);
	const [testUserInfo, setTestUserInfo] = useState(initialTestUserInfo);

	const formRef = useRef(null);

	const allGrades = useMemo(
		() => [
			{id: 2, value: '2', label: '2'},
			{id: 3, value: '3', label: '3'},
			{id: 4, value: '4', label: '4'},
			{id: 5, value: '5', label: '5'},
			{id: 6, value: '6', label: '6'},
		],
		[]
	);

	const showReadingTestOption = useMemo(() => {
		const g = Number(testUserInfo.education?.youthGrade || 0);
		return g === 2 || g === 3;
	}, [testUserInfo.education]);

	const updateTestUser = useCallback((categoryKey, key, value) => {
		setTestUserInfo((prev) => {
			if (categoryKey) {
				const category = updateObject(prev[categoryKey] || {}, {
					[key]: value,
				});
				return updateObject(prev, {[categoryKey]: category});
			}
			return updateObject(prev, {[key]: value});
		});
	}, []);

	const updateTestUserBirthDate = useCallback(
		(key, value, keyBirth, valBirth) => {
			setTestUserInfo((prev) =>
				updateObject(prev, {
					[key]: value,
					[keyBirth]: valBirth,
				})
			);
		},
		[]
	);

	const handleFormChange = useCallback(() => {
		if (!formRef.current) return;
		const valid = formRef.current.checkValidity();
		if (valid !== isFormValid && !dateIsWrong) setIsFormValid(valid);
	}, [isFormValid, dateIsWrong]);

	const sendCodeAndInfoUsingEmail = useCallback(
		(event, testUser, pin) => {
			event.preventDefault();

			const {age} = testUser;
			if (age.years < 3 || age.years > 90) {
				setDateIsWrong(true);
				return;
			}

			const {id, testKey} = props.infoPanelTestKey;
			const {processTestResults, testResults} = props;

			const nextTestResults = updateItemInArray(
				testResults,
				id,
				(currentTestResult) =>
					updateObject(currentTestResult, {
						distributedAt: moment(),
					})
			);

			// In original code: computed but not used; keeping for parity
			// If processTestResults has side effects, this preserves behavior.
			// eslint-disable-next-line no-unused-vars
			const nextBatchedTestResults = processTestResults(nextTestResults);

			props.onDistributeKeyAndStoreInfo(
				isNorming,
				testKey,
				testUser,
				showReadingTestOption ? excludeReading : 'no',
				pin
			);
		},
		[excludeReading, isNorming, props, showReadingTestOption]
	);

	// Safely pull data if test user already exists in props.testResults
	const {testResults} = props;
	const {id: resultsId, testUser: existingUser} = testResults || {};
	const emailFromResults = existingUser?.email;
	const firstNameFromResults = existingUser?.firstName;
	const lastNameFromResults = existingUser?.lastName;

	const {pinCode, testKey: keyLabel} = props.infoPanelTestKey || {};

	return (
		<div
			className="lit-modal-candidate"
			onClick={() => {
				props.closeWindow();
			}}
		>
			<div
				className="lit-modal__continent"
				onClick={(event) => {
					event.stopPropagation();
				}}
			>
				<div style={{width: '85%', margin: 'auto'}}>
					<div
						className="lit-candidate-info"
						onClick={() => {
							props.closeWindow();
						}}
					>
						<div
							className="lit-candidate-container"
							onClick={(event) => {
								event.stopPropagation();
							}}
						>
							<form
								className="lit-input-candidate"
								onChange={handleFormChange}
								onSubmit={(e) => {
									e.preventDefault();
									sendCodeAndInfoUsingEmail(
										e,
										testUserInfo,
										pinCode
									);
								}}
								ref={formRef}
							>
								<div className="lit-info__main-container">
									<div className="lit-info__main-container__wrapper1">
										<div className="lit-info__field">
											<h3
												className="lit-candidate-info-header__bolder"
												style={{
													fontSize: '15px',
													color: 'black',
													fontWeight: 'bold',
													padding: '10px',
												}}
											>
												{'Angaben zur Testperson'}
												<br />
												{'(Schülerin/Schüler)'}
											</h3>

											{/* First name */}
											<div className="lit-candidate-label">
												<label
													className="lit-test__heading"
													htmlFor={`firstName-${resultsId}`}
												>
													{strings.firstname}
												</label>
												<div>
													<input
														className="lit-input__field"
														id={`firstName-${resultsId}`}
														name="firstName"
														placeholder="Mala"
														value={
															testUserInfo.firstName ||
															firstNameFromResults ||
															''
														}
														onChange={(e) =>
															updateTestUser(
																null,
																'firstName',
																e.target.value
															)
														}
														required
														type="text"
													/>
												</div>
											</div>

											{/* Last name */}
											<div className="lit-candidate-label">
												<label
													className="lit-test__heading"
													htmlFor="lastName"
												>
													{strings.lastname}
												</label>
												<div>
													<input
														className="lit-input__field"
														id="lastName"
														name="lastName"
														placeholder="Hensen"
														value={
															testUserInfo.lastName ||
															lastNameFromResults ||
															''
														}
														onChange={(e) =>
															updateTestUser(
																null,
																'lastName',
																e.target.value
															)
														}
														required
														type="text"
													/>
												</div>
											</div>

											{/* Email */}
											<div className="lit-candidate-label">
												<label
													className="lit-test__heading"
													htmlFor={`email-${resultsId}`}
												>
													{strings.email}
												</label>
												<div>
													<input
														className="lit-input__field"
														id={`email-${resultsId}`}
														name="email"
														placeholder="mala@dysmate.de"
														value={
															testUserInfo.email ||
															emailFromResults ||
															''
														}
														onChange={(e) =>
															updateTestUser(
																null,
																'email',
																e.target.value
															)
														}
														type="email"
													/>
												</div>
											</div>
										</div>

										{/* Gender */}
										<div className="lit-info__field">
											<div className="lit-candidate-label-gender">
												<label className="lit-test__heading">
													{strings.gender}
												</label>
												<div className="lit-input__field">
													{genders.map(
														({label, value}) => (
															<span
																className="lit-radio"
																key={value}
															>
																<input
																	checked={
																		testUserInfo.gender ===
																		value
																	}
																	className="lit-radio__field"
																	id={`gender-${value}`}
																	name="gender"
																	value={
																		value
																	}
																	onChange={() =>
																		updateTestUser(
																			null,
																			'gender',
																			value
																		)
																	}
																	required
																	type="radio"
																/>
																<label
																	className="lit-radio__label"
																	htmlFor={`gender-${value}`}
																>
																	{label}
																</label>
															</span>
														)
													)}
												</div>
											</div>
										</div>

										{/* Is foreign (first language not German) */}
										<div className="lit-info__field">
											<div className="lit-candidate-label-gender">
												<label className="lit-test__heading">
													{
														'Spricht die Testperson eine andere Erstsprache als Deutsch?'
													}
												</label>
												<div className="lit-input__field">
													{yesAndNo.map(
														({label, value}) => (
															<span
																className="lit-radio"
																key={value}
															>
																<input
																	checked={
																		testUserInfo.isForeign ===
																		value
																	}
																	className="lit-radio__field"
																	id={`isForeign-${value}`}
																	name="isForeign"
																	value={
																		value
																	}
																	onChange={() => {
																		updateTestUser(
																			null,
																			'isForeign',
																			value
																		);
																		updateTestUser(
																			HOME_LANGUAGE,
																			GREW_UP_IN_BILINGUAL_HOME,
																			value ===
																				'yes'
																		);
																	}}
																	required
																	type="radio"
																/>
																<label
																	className="lit-radio__label"
																	htmlFor={`isForeign-${value}`}
																>
																	{label}
																</label>
															</span>
														)
													)}
												</div>
											</div>
										</div>

										{/* Birth date / age */}
										<div className="lit-info__field">
											<div className="lit-candidate-label">
												<label
													className="lit-test__heading"
													htmlFor="birthDate"
												>
													{strings.birthDate}
												</label>
												<div className="lit-input__field">
													<input
														type="date"
														placeholder="dd-mm-yyyy"
														id="birthDate"
														name="age"
														value={
															testUserInfo.birthDate ||
															''
														}
														onChange={(e) => {
															const value =
																e.target.value;
															const now =
																moment(); // compute *now* at change time
															const years =
																now.diff(
																	value,
																	'years'
																);
															const months =
																now.diff(
																	value,
																	'months'
																) % 12;
															const age = {
																years,
																months,
															};

															setDateIsWrong(
																!(
																	years > 2 &&
																	years < 90
																)
															);

															updateTestUserBirthDate(
																'age',
																age,
																'birthDate',
																value
															);
														}}
														required
													/>
												</div>
												{dateIsWrong && (
													<span
														style={{
															color: 'DarkOrange',
															fontSize: '14px',
														}}
													>
														<i>{`Fødselsdato er ikke riktig`}</i>
													</span>
												)}
											</div>

											{/* Grade */}
											<div className="lit-candidate-label-grade">
												<label className="lit-test__heading">
													{strings.classGrade}
												</label>
												<div>
													<select
														className="lit-input__field-trinn"
														id={`email-invite-${resultsId}`}
														name="youthGrade"
														required
														value={
															testUserInfo
																.education
																.youthGrade
														}
														onChange={(e) => {
															const education = {
																youthGrade:
																	e.target
																		.value,
															};
															updateTestUser(
																null,
																'education',
																education
															);
															// If leaving the 2/3 band, reset excludeReading to "no" for consistency
															const g = Number(
																e.target.value
															);
															if (
																!(
																	g === 2 ||
																	g === 3
																)
															)
																setExcludeReading(
																	'no'
																);
														}}
													>
														<option
															value=""
															disabled
														>
															{
																'-Klassenstufe wählen-'
															}
														</option>
														{allGrades.map((gr) => (
															<option
																id={gr.id}
																key={gr.id}
																value={gr.value}
															>
																{gr.label}
															</option>
														))}
													</select>
												</div>
											</div>
										</div>

										{/* Pin & key */}
										<div className="lit-info__field">
											<div className="lit-candidate-label-pin-key">
												<label className="lit-test__heading">
													{'PIN'}
												</label>
												<div className="lit-input__field">
													<span className="lit-candidate-pin">
														{pinCode
															? pinCode
															: 'Skjult'}
													</span>
												</div>
											</div>
											<div className="lit-candidate-label-pin-key">
												<label className="lit-test__heading">
													{'Testschlüssel'}
												</label>
												<div className="lit-input__field">
													<span className="lit-candidate-pin">
														{keyLabel}
													</span>
												</div>
											</div>
										</div>
									</div>

									{/* Right column */}
									<div className="lit-info__main-container__wrapper2">
										<div className="lit-info__field-text">
											<p>
												{
													strings.testCandidateTakeTestWith
												}
											</p>
											<li>{strings.gotoDysmtateSite}</li>
											<li>
												{
													strings.testCommenceInstructions1
												}
											</li>

											<p>
												{
													strings.testCommenceInstructions4
												}
											</p>
											<p>
												{
													strings.testCommenceInstructions5
												}
											</p>

											<li>
												{
													strings.testCommenceInstructions5_1a
												}
												<a
													href="https://www.dysmate.de"
													target="_blank"
													rel="noreferrer"
													style={{
														color: '#1a73e8',
														textDecoration:
															'underline',
													}}
												>
													{
														strings.testCommenceInstructions5_1_linkText
													}
												</a>
												{
													strings.testCommenceInstructions5_1b
												}
											</li>
											<li>
												{
													strings.testCommenceInstructions5_2
												}
											</li>
											<li>
												{
													strings.testCommenceInstructions5_3
												}
											</li>
											<li>
												{
													strings.testCommenceInstructions5_4
												}
											</li>
											<li>
												{
													strings.testCommenceInstructions5_5
												}
											</li>
										</div>
										<br />

										<div className="lit-info__field-fifth">
											<div>
												<span className="lit-fill-out-info-consent">
													<input
														type="checkbox"
														name="agreedToPrivacyPolicy"
														required
														className="user_checkbox2"
													/>{' '}
													{strings.iConsent}
												</span>
												<br />
												{strings.formatString(
													strings.constTestChild,
													<a
														key="privacy-link"
														target="_blank"
														rel="noopener noreferrer"
														href="https://www.dysmate.de/hilfe/"
													>
														<u>
															{
																strings.privacyPolicy
															}
														</u>
													</a>
												)}
											</div>
											<div className="lit-btn-submit-from">
												<button
													className={
														isFormValid
															? 'lit-btn lit-btn bg-primary-glow'
															: 'lit-btn lit-btn bg-primary-dark'
													}
													onClick={() => {}}
												>
													{strings.sendtest}
												</button>
											</div>
										</div>
									</div>
								</div>
							</form>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default CandidateInfo;
